import { Notebook, Comment } from "../types";

// Определяем API_URL с учетом типов для process.env
declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

export const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Функция для получения заголовков с токеном авторизации
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// Функция для обработки ответа от сервера
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }

  return response.text();
};

// Функция для преобразования snake_case в camelCase
export const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => toCamelCase(item));
  }

  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
        letter.toUpperCase()
      );
      acc[camelKey] = toCamelCase(obj[key]);
      return acc;
    }, {} as any);
  }

  return obj;
};

// Функция для загрузки ноутбука
export const uploadNotebook = async (
  file: File,
  title: string,
  lectureId?: number
): Promise<Notebook> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("title", title);

  if (lectureId) {
    formData.append("lecture_id", lectureId.toString());
  }

  try {
    const token = localStorage.getItem("token");
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(`${API_URL}/notebooks/upload`, {
      method: "POST",
      headers,
      body: formData,
    });

    const data = await handleResponse(response);
    return toCamelCase(data);
  } catch (error) {
    console.error("Ошибка при загрузке ноутбука:", error);
    throw error;
  }
};

// Функция для получения ноутбука по ID
export const getNotebook = async (id: string | number): Promise<Notebook> => {
  try {
    const response = await fetch(`${API_URL}/notebooks/${id}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    const data = await handleResponse(response);
    return toCamelCase(data);
  } catch (error) {
    console.error(`Ошибка при получении ноутбука с ID ${id}:`, error);
    throw error;
  }
};

export const addComment = async (
  notebookId: number,
  comment: {
    text: string;
    cellId: string;
    lineNumber: number;
  }
): Promise<Comment> => {
  const response = await fetch(`${API_URL}/notebooks/${notebookId}/comments/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      text: comment.text,
      cell_id: comment.cellId,
      line_number: comment.lineNumber,
    }),
  });
  return toCamelCase(await handleResponse(response));
};

export const getComments = async (
  notebookId: number,
  viewToken?: string | null
): Promise<Comment[]> => {
  let url = `${API_URL}/notebooks/${notebookId}/comments/`;
  if (viewToken) {
    url += `?token=${viewToken}`;
  }
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return toCamelCase(await handleResponse(response));
};

export const updateComment = async (
  notebookId: number,
  commentId: number,
  text: string
): Promise<Comment> => {
  const response = await fetch(
    `${API_URL}/notebooks/${notebookId}/comments/${commentId}`,
    {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify({ text }),
    }
  );
  return toCamelCase(await handleResponse(response));
};

export const deleteComment = async (
  notebookId: number,
  commentId: number
): Promise<void> => {
  await fetch(`${API_URL}/notebooks/${notebookId}/comments/${commentId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
};

export const deleteNotebook = async (notebookId: number): Promise<void> => {
  const response = await fetch(`${API_URL}/notebooks/${notebookId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(
      `Ошибка при пометке ноутбука как удаленного: ${response.status}`
    );
  }
};

// Функция для экспорта ноутбука
export const exportNotebook = async (
  notebookId: number,
  format: "html" | "pdf"
): Promise<Blob> => {
  const response = await fetch(
    `${API_URL}/notebooks/${notebookId}/export/${format}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Ошибка при экспорте: ${response.status}`);
  }

  return await response.blob();
};

export const generateShareLink = async (
  notebookId: number
): Promise<{ token: string }> => {
  const response = await fetch(`${API_URL}/notebooks/${notebookId}/share`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return toCamelCase(await handleResponse(response));
};

// Этого эндпоинта нет, функция не работает
export const getNotebookByToken = async (token: string): Promise<Notebook> => {
  try {
    const response = await fetch(`${API_URL}/review/${token}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await handleResponse(response);
    return toCamelCase(data);
  } catch (error) {
    console.error("Error fetching notebook by token:", error);
    throw error;
  }
};

export const getNotebookWithCommentsByToken = async (
  token: string
): Promise<Notebook> => {
  try {
    const response = await fetch(`${API_URL}/share/${token}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Ошибка при получении ноутбука: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении ноутбука:", error);
    throw error;
  }
};

// Функция для предпросмотра ноутбука с комментариями
export const previewNotebookWithComments = async (
  notebookId: string | number
): Promise<Notebook> => {
  try {
    const response = await fetch(`${API_URL}/notebooks/${notebookId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await handleResponse(response);
    return toCamelCase(data);
  } catch (error) {
    console.error("Error previewing notebook with comments:", error);
    throw error;
  }
};

export const getCourses = async (): Promise<any[]> => {
  const response = await fetch(`${API_URL}/courses/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return toCamelCase(await handleResponse(response));
};

export const getCourseRuns = async (courseId: number): Promise<any[]> => {
  const response = await fetch(`${API_URL}/courses/${courseId}/runs/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return toCamelCase(await handleResponse(response));
};

export const getLectures = async (courseRunId: number): Promise<any[]> => {
  const response = await fetch(
    `${API_URL}/course-runs/${courseRunId}/lectures/`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );
  return toCamelCase(await handleResponse(response));
};

export const getNotebooks = async (lectureId: number): Promise<any[]> => {
  const response = await fetch(`${API_URL}/lectures/${lectureId}/notebooks/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return toCamelCase(await handleResponse(response));
};

// Функция для скачивания ноутбука как zip
export const downloadNotebookAsZip = async (
  notebookId: number
): Promise<Blob> => {
  const response = await fetch(
    `${API_URL}/notebooks/${notebookId}/download/zip`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Ошибка при скачивании: ${response.status}`);
  }

  return await response.blob();
};

// Функция для скачивания всех ноутбуков как zip
export const downloadAllNotebooksAsZip = async (
  lectureId: number
): Promise<Blob> => {
  const response = await fetch(
    `${API_URL}/lectures/${lectureId}/notebooks/download/zip`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Ошибка при скачивании: ${response.status}`);
  }

  return await response.blob();
};

/**
 * Получение заметок преподавателя для ноутбука
 * @param notebookId ID ноутбука
 * @returns Заметки преподавателя
 */
export const getTeacherNotes = async (notebookId: number): Promise<string> => {
  try {
    const response = await fetch(
      `${API_URL}/notebooks/${notebookId}/teacher-notes`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Ошибка при получении заметок преподавателя: ${response.status}`
      );
    }

    const data = await response.json();
    return data.notes;
  } catch (error) {
    console.error("Ошибка при получении заметок преподавателя:", error);
    throw error;
  }
};

/**
 * Сохранение заметок преподавателя для ноутбука
 * @param notebookId ID ноутбука
 * @param notes Текст заметок
 * @returns Обновленные заметки преподавателя
 */
export const saveTeacherNotes = async (
  notebookId: number,
  notes: string
): Promise<string> => {
  try {
    const response = await fetch(
      `${API_URL}/notebooks/${notebookId}/teacher-notes`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ notes }),
      }
    );

    if (!response.ok) {
      throw new Error(
        `Ошибка при сохранении заметок преподавателя: ${response.status}`
      );
    }

    const data = await response.json();
    return data.notes;
  } catch (error) {
    console.error("Ошибка при сохранении заметок преподавателя:", error);
    throw error;
  }
};

// Функции для работы с датасетами
export const getDatasets = async (): Promise<any[]> => {
  const response = await fetch(`${API_URL}/datasets/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return toCamelCase(await handleResponse(response));
};

export const getDataset = async (datasetId: number): Promise<any> => {
  const response = await fetch(`${API_URL}/datasets/${datasetId}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return toCamelCase(await handleResponse(response));
};

export const createDataset = async (
  title: string,
  description?: string
): Promise<any> => {
  const response = await fetch(`${API_URL}/datasets/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ title, description }),
  });
  return toCamelCase(await handleResponse(response));
};

export const updateDataset = async (
  datasetId: number,
  title: string,
  description?: string
): Promise<any> => {
  const response = await fetch(`${API_URL}/datasets/${datasetId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ title, description }),
  });
  return toCamelCase(await handleResponse(response));
};

export const deleteDataset = async (datasetId: number): Promise<void> => {
  await fetch(`${API_URL}/datasets/${datasetId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
};

export const getLectureDataset = async (lectureId: number): Promise<any> => {
  const response = await fetch(`${API_URL}/lectures/${lectureId}/dataset`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok && response.status !== 404) {
    throw new Error(`Ошибка при получении датасета лекции: ${response.status}`);
  }

  if (response.status === 404 || response.status === 204) {
    return null;
  }

  return toCamelCase(await handleResponse(response));
};

export const setLectureDataset = async (
  lectureId: number,
  datasetId: number | null
): Promise<any> => {
  try {
    const response = await fetch(`${API_URL}/lectures/${lectureId}/dataset`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ dataset_id: datasetId }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Ошибка ответа:", errorText);
      throw new Error(
        errorText || `Ошибка ${response.status}: ${response.statusText}`
      );
    }

    try {
      const result = await handleResponse(response);
      return toCamelCase(result);
    } catch (parseError) {
      console.error("Ошибка при обработке ответа:", parseError);
      throw parseError;
    }
  } catch (error) {
    console.error("Ошибка при выполнении запроса:", error);
    throw error;
  }
};

export const addCommentToDataset = async (
  datasetId: number,
  commentId: number
): Promise<any> => {
  const response = await fetch(
    `${API_URL}/datasets/${datasetId}/comments/${commentId}`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );
  return toCamelCase(await handleResponse(response));
};

export const removeCommentFromDataset = async (
  datasetId: number,
  commentId: number
): Promise<void> => {
  await fetch(`${API_URL}/datasets/${datasetId}/comments/${commentId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
};

export const getCommentDatasets = async (commentId: number): Promise<any[]> => {
  const response = await fetch(`${API_URL}/comments/${commentId}/datasets`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return toCamelCase(await handleResponse(response));
};

export const getCommentContext = async (
  commentId: number,
  contextLines: number = 5
): Promise<any> => {
  try {
    const response = await fetch(
      `${API_URL}/comments/${commentId}/context?context_lines=${contextLines}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );

    const data = await handleResponse(response);
    return toCamelCase(data);
  } catch (error) {
    console.error(
      `Ошибка при получении контекста комментария с ID ${commentId}:`,
      error
    );
    throw error;
  }
};

// Функция для скачивания датасета в формате .json.gz
export const downloadDataset = async (datasetId: number): Promise<void> => {
  const response = await fetch(`${API_URL}/datasets/${datasetId}/download`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Ошибка при скачивании датасета: ${response.status}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dataset_${datasetId}.zip`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const getRagEligibleNotebooks = async (
  lectureId: number | ""
): Promise<number[]> => {
  const response = await fetch(
    `${API_URL}/notebooks/rag-eligibility/?lecture_id=${lectureId}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);
  return toCamelCase(data).eligibleNotebooks || [];
};

export const canApplyRagToNotebook = async (
  notebookId: number
): Promise<{
  canApplyRag: boolean;
  reason?: string;
  notebookId?: number;
  notebookTitle?: string;
  datasetId?: number;
  datasetTitle?: string;
}> => {
  const response = await fetch(
    `${API_URL}/notebooks/${notebookId}/can_apply_rag`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);
  return toCamelCase(data);
};

// Функция для запуска RAG для ноутбука
export const applyRagToNotebook = async (
  notebookId: number,
  scoreThreshold: number = 0.6
): Promise<{
  notebookId: number;
  notebookTitle: string;
  datasetId: number;
  datasetTitle: string;
  status: string;
  message: string;
}> => {
  const response = await fetch(
    `${API_URL}/notebooks/${notebookId}/apply_rag?score_threshold=${scoreThreshold}`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);
  return toCamelCase(data);
};

// Функция для получения статуса выполнения RAG для ноутбука
export const getRagStatus = async (
  notebookId: number
): Promise<{
  notebookId: number;
  notebookTitle: string;
  commentsCount: number;
  status: string;
}> => {
  const response = await fetch(
    `${API_URL}/notebooks/${notebookId}/rag_status`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);
  return toCamelCase(data);
};

// Функция для запуска обработки эмбеддингов датасета
export const processDatasetEmbeddings = async (
  datasetId: number
): Promise<{
  message: string;
  status: string;
  embeddingsStatus: string;
  dataset_id: number;
}> => {
  const response = await fetch(
    `${API_URL}/datasets/${datasetId}/process_embeddings_async`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);
  return toCamelCase(data);
};

// Функция для получения статуса обработки эмбеддингов датасета
export const getDatasetEmbeddingsStatus = async (
  datasetId: number
): Promise<{
  status: string;
  message: string;
  embeddingsStatus: string;
  dataset_id: number;
  dataset_title: string;
  stats: {
    totalBlocks: number;
    blocksWithEmbeddings: number;
    percentage: number;
  };
  progress?: {
    processed: number;
    total: number;
    percentage: number;
  };
}> => {
  const response = await fetch(
    `${API_URL}/datasets/${datasetId}/embeddings_status`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);
  return toCamelCase(data);
};

// Функция для сброса статуса обработки эмбеддингов датасета
export const resetDatasetEmbeddingsStatus = async (
  datasetId: number
): Promise<{
  message: string;
  status: string;
  embeddingsStatus: string;
  dataset_id: number;
}> => {
  const response = await fetch(
    `${API_URL}/datasets/${datasetId}/reset_embeddings_status`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);
  return toCamelCase(data);
};

// Функция для получения статистики эмбеддингов датасета
export const getDatasetEmbeddingsStats = async (
  datasetId: number
): Promise<{
  totalBlocks: number;
  blocksWithEmbeddings: number;
  percentage: number;
}> => {
  const response = await fetch(
    `${API_URL}/datasets/${datasetId}/embeddings_status`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  const data = await handleResponse(response);
  return toCamelCase(data).stats;
};

export const sendNotebookCheckTime = async (
  notebookId: number,
  timeMs: number
): Promise<void> => {
  const response = await fetch(`${API_URL}/notebooks/check_time`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      notebook_id: notebookId,
      time_ms: timeMs,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
};

export const getLectureAnalytics = async (lectureId: number): Promise<any> => {
  const response = await fetch(`${API_URL}/analytics/lecture/${lectureId}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

export const getLectureGCThreads = async (lectureId: number): Promise<any> => {
  const response = await fetch(`${API_URL}/gc/lecture/${lectureId}/threads/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  const data = await response.json();
  return toCamelCase(data);
};

export const setGCThreadStatus = async (
  threadId: number,
  status: string
): Promise<any> => {
  const response = await fetch(`${API_URL}/gc/thread/status/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      thread_id: threadId,
      status: status,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

export const setGCThreadComment = async (
  threadId: number,
  comment: string
): Promise<any> => {
  const response = await fetch(`${API_URL}/gc/thread/comment/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      thread_id: threadId,
      comment: comment,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

export const setGCThreadMark = async (
  threadId: number,
  mark: number | null
): Promise<any> => {
  const response = await fetch(`${API_URL}/gc/thread/mark/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      thread_id: threadId,
      mark: mark,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

export const setGCThreadReviewer = async (
  threadId: number,
  reviewer: string | null
): Promise<any> => {
  const response = await fetch(`${API_URL}/gc/thread/reviewer/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      thread_id: threadId,
      reviewer: reviewer,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

export const getLecturesWithGC = async (courseRunId: number): Promise<any> => {
  const response = await fetch(
    `${API_URL}/lectures_gc/?course_run_id=${courseRunId}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

export const setGCLessonToLecture = async (
  lectureId: number,
  gcLessonId: number
): Promise<any> => {
  const response = await fetch(`${API_URL}/lectures_gc/gc_lesson/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      lecture_id: lectureId,
      gc_lesson_id: gcLessonId,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

export const syncLectureWithGCLesson = async (
  lectureId: number,
  gcLessonId: number
): Promise<any> => {
  const response = await fetch(`${API_URL}/gc/sync_lecture/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      lecture_id: lectureId,
      gc_lesson_id: gcLessonId,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Ошибка ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};
